/* eslint-disable @typescript-eslint/no-use-before-define */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IBleState } from '../state/IBleState';
import { AppThunk } from '../store';
import { GhostBle } from '../../ble/GhostBle';
import { sleep } from '../../helpers/sleep';

const initialState: IBleState = {
  connecting: false,
  connected: false,
  hasConnected: false,
  updating: false,
  connectAttempts: 0,
  locked: false,
  locking: false,
  authKey: localStorage.getItem('ghostmv1.auth') || '',
  status: 0,
  battery: 0,
  profile: 0,
  temperatures: [170, 185, 200, 210, 220, 220],
};

const disconnect = (): AppThunk<void> => async (dispatch): Promise<void> => {
  await GhostBle.disconnect();
  dispatch(disconnected());
};

const connect = (allDevices = false): AppThunk<number> => async (dispatch, getState): Promise<number> => {
  try {
    await GhostBle.connect(allDevices);
    dispatch(connectSuccess());
    dispatch(startNotifications());
    return -1;
  } catch (e) {
    if (e.message === 'INVALID_KEY') {
      return -2;
    } else {
      return getState().ble.connectAttempts;
    }
  }
};

const pair = (allDevices: boolean): AppThunk<boolean> => async (dispatch): Promise<boolean> => {
  try {
    await GhostBle.pair(allDevices);
    dispatch(connectSuccess());
    dispatch(startNotifications());
    return true;
  } catch (e) {
    return false;
  }
};

const updateCurrentProfile = (profile: number): AppThunk<number> => async (dispatch, getState): Promise<number> => {
  try {
    if (getState().ble.locked) {
      return profile;
    }
    await dispatch(pauseNotifications());
    dispatch(startUpdateProfile());
    const newProfile = await GhostBle.updateCurrentProfile(profile);
    if (newProfile) {
      dispatch(setCurrentProfileForce(newProfile));
      return newProfile;
    } else {
      return profile;
    }
  } finally {
    await dispatch(startNotifications());
    dispatch(completeUpdateProfile());
  }
};

const updateTemperature = (values: number[]): AppThunk<boolean> => async (dispatch, getState): Promise<boolean> => {
  try {
    if (getState().ble.locked) {
      return false;
    }
    await dispatch(pauseNotifications());
    dispatch(startUpdate());
    await GhostBle.updateTemperature(values);
    dispatch(setTemperatures(values));
    return true;
  } catch {
    return false;
  } finally {
    await dispatch(startNotifications());
    dispatch(completeUpdate());
  }
};

const lock = (): AppThunk<boolean> => async (dispatch, getState): Promise<boolean> => {
  try {
    await dispatch(pauseNotifications());
    dispatch(requestLock());
    const success = await GhostBle.lock();
    if (success) {
      dispatch(setStatus(getState().ble.status | 0x1));
    }
    return success || false;
  } finally {
    dispatch(completeLock());
    await dispatch(startNotifications());
    dispatch(completeUpdate());
  }
};

const unlock = (): AppThunk<boolean> => async (dispatch, getState): Promise<boolean> => {
  try {
    await dispatch(pauseNotifications());
    dispatch(requestUnlock());
    const success = await GhostBle.unlock();
    if (success) {
      dispatch(setStatus(getState().ble.status & 0xfe));
    }
    return success || false;
  } finally {
    dispatch(completeUnlock());
    await dispatch(startNotifications());
    dispatch(completeUpdate());
  }
};

const startNotifications = (): AppThunk<void> => async (): Promise<void> => {
  await GhostBle.startNotifications();
  await sleep(250);
};

const pauseNotifications = (): AppThunk<void> => async (): Promise<void> => {
  await GhostBle.pauseNotifications();
  await sleep(250);
};

const bleSlice = createSlice({
  name: 'BLE',
  initialState,
  reducers: {
    startUpdate: (state: IBleState): IBleState => ({
      ...state,
      updating: true,
    }),
    completeUpdate: (state: IBleState): IBleState => ({
      ...state,
      updating: false,
    }),
    startUpdateProfile: (state: IBleState): IBleState => ({
      ...state,
      updating: true,
    }),
    completeUpdateProfile: (state: IBleState): IBleState => ({
      ...state,
      updating: false,
    }),
    connectStart: (state: IBleState): IBleState => ({
      ...state,
      connecting: true,
      connected: false,
      error: undefined,
      connectAttempts: state.connectAttempts + 1,
    }),
    connectSuccess: (state: IBleState): IBleState => ({
      ...state,
      connecting: false,
      connected: true,
      hasConnected: true,
      error: undefined,
      connectAttempts: 0,
    }),
    connectFailed: (state: IBleState, action: PayloadAction<string>): IBleState => ({
      ...state,
      connecting: false,
      connected: false,
      error: action.payload,
    }),
    disconnected: (state: IBleState): IBleState => ({
      ...state,
      connecting: false,
      connected: false,
      error: undefined,
      connectAttempts: 0,
    }),
    setAuthKey: (state: IBleState, action: PayloadAction<string>): IBleState => ({
      ...state,
      authKey: action.payload,
    }),
    setStatus: (state: IBleState, action: PayloadAction<number>): IBleState => ({
      ...state,
      status: action.payload,
      locked: (action.payload & 0x1) === 0x1,
    }),
    setBatteryLevel: (state: IBleState, action: PayloadAction<number>): IBleState => ({
      ...state,
      battery: action.payload,
    }),
    setCurrentProfileForce: (state: IBleState, action: PayloadAction<number>): IBleState => {
      return {
        ...state,
        profile: action.payload,
      };
    },
    setCurrentProfile: (state: IBleState, action: PayloadAction<number>): IBleState => {
      if (state.updating) {
        return state;
      } else {
        return {
          ...state,
          profile: action.payload,
        };
      }
    },
    setTemperatures: (state: IBleState, action: PayloadAction<number[]>): IBleState => ({
      ...state,
      temperatures: action.payload,
    }),
    clearError: (state: IBleState): IBleState => ({
      ...state,
      error: undefined,
    }),
    requestLock: (state: IBleState): IBleState => ({
      ...state,
      updating: true,
      locking: true,
    }),
    requestUnlock: (state: IBleState): IBleState => ({
      ...state,
      updating: true,
      locking: true,
    }),
    completeLock: (state: IBleState): IBleState => ({
      ...state,
      updating: false,
      locking: false,
      locked: true,
    }),
    completeUnlock: (state: IBleState): IBleState => ({
      ...state,
      updating: false,
      locking: false,
      locked: false,
    }),
  },
});

export {
  connect,
  disconnect,
  pair,
  updateCurrentProfile,
  updateTemperature,
  startNotifications,
  pauseNotifications,
  lock,
  unlock,
};

export const {
  connectStart,
  connectSuccess,
  connectFailed,
  disconnected,
  setAuthKey,
  setStatus,
  setBatteryLevel,
  setCurrentProfile,
  setCurrentProfileForce,
  setTemperatures,
  clearError,
  startUpdate,
  completeUpdate,
  startUpdateProfile,
  completeUpdateProfile,
  requestLock,
  requestUnlock,
  completeLock,
  completeUnlock,
} = bleSlice.actions;
export default bleSlice.reducer;
